import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Button,
  FormHelperText,
  Box,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import i18next from 'i18next';
import { EOfferRejectionReason } from '__generated__/graphql';

const REJECTION_REASON_CODES: EOfferRejectionReason[] = [
  EOfferRejectionReason.NOT_ENOUGH_TIME,
  EOfferRejectionReason.NOT_ENOUGH_SEMINAR_ROOMS,
  EOfferRejectionReason.REQUESTED_TIME_PERIOD_NOT_AVAILABLE,
  EOfferRejectionReason.REQUEST_NOT_FITTING,
  EOfferRejectionReason.OTHER,
];

function rejectionReasonLabel(code: EOfferRejectionReason): string {
  return String(i18next.t(`rejection-reason-${code}`));
}

interface OfferRejectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: { code: EOfferRejectionReason; customText?: string }) => void;
  loading?: boolean;
  offerStatus?: string;
  versionStatus?: string;
}

export function OfferRejectionDialog({
  open,
  onClose,
  onSubmit,
  loading = false
  ,
  offerStatus,
  versionStatus,
}: OfferRejectionDialogProps) {
  const theme = useTheme();
  const [selectedReason, setSelectedReason] = useState<EOfferRejectionReason | ''>('');
  const [customText, setCustomText] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleClose = () => {
    setSelectedReason('');
    setCustomText('');
    setError('');
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      setError(i18next.t('rejection-error-no-reason') as string);
      return;
    }

    if (selectedReason === EOfferRejectionReason.OTHER) {
      const trimmedText = customText.trim();
      if (!trimmedText) {
        setError(i18next.t('rejection-error-no-text') as string);
        return;
      }
      if (trimmedText.length < 5) {
        setError(i18next.t('rejection-error-min-length') as string);
        return;
      }
    }

    onSubmit({
      code: selectedReason,
      customText: selectedReason === EOfferRejectionReason.OTHER ? customText.trim() : undefined,
    });

    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {(versionStatus === 'CR_WAITFORHOTELAPPROVAL' || versionStatus === 'CR_WAITFORCLIENTAPPROVAL') && offerStatus === 'APPROVED'
          ? i18next.t('offerconfirm-rejectcr-reservation-title')
          : i18next.t('rejection-dialog-title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(versionStatus === 'CR_WAITFORHOTELAPPROVAL' || versionStatus === 'CR_WAITFORCLIENTAPPROVAL') && offerStatus === 'APPROVED'
              ? i18next.t('offerconfirm-rejectcr-reservation-text')
              : i18next.t('rejection-dialog-subtitle')}
          </Typography>

          <FormControl component="fieldset" error={!!error} fullWidth>
            <RadioGroup
              value={selectedReason}
              onChange={(e) => {
                setSelectedReason(e.target.value as EOfferRejectionReason);
                setError('');
              }}
            >
              {REJECTION_REASON_CODES.map(code => (
                <FormControlLabel
                  key={code}
                  value={code}
                  control={<Radio />}
                  label={rejectionReasonLabel(code)}
                  disabled={loading}
                />
              ))}
            </RadioGroup>

            {selectedReason === EOfferRejectionReason.OTHER && (
              <TextField
                multiline
                rows={3}
                fullWidth
                margin="normal"
                label={i18next.t('rejection-reason-custom-label')}
                value={customText}
                onChange={(e) => {
                  setCustomText(e.target.value);
                  setError('');
                }}
                error={!!error}
                helperText={error || `${customText.length}/5 ${i18next.t('rejection-reason-custom-min-chars')}`}
                disabled={loading}
              />
            )}

            {error && selectedReason !== EOfferRejectionReason.OTHER && (
              <FormHelperText>{error}</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {i18next.t('rejection-dialog-cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
        >
          {i18next.t('rejection-dialog-confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}